import React from 'react';
import { Autocomplete, TextField, Checkbox } from '@mui/material';
import { useSelector } from 'react-redux';
import './AirportInput.css';

// Component for selecting departure airports with region grouping functionality
function AirportInput({ value, onChange }) {
  // Get airports data from Redux store
  const airports = useSelector((state) => state.search.airports);
  
  // Memoized function to group airports by their regions
  const groupedOptions = React.useMemo(() => {
    // Filter out region entries
    const regions = airports.filter(a => a.isRegion);
    // Map each region to an object containing its child airports
    return regions.map(region => ({
      region,
      airports: airports.filter(a => 
        !a.isRegion && region.childAirports?.some(child => child.code === a.code)
      )
    }));
  }, [airports]);

  // Memoized function to create the full list of selectable options
  const filteredOptions = React.useMemo(() => {
    const regions = airports.filter(a => a.isRegion);
    const nonRegionAirports = airports.filter(a => !a.isRegion);
    
    // Sort airports to maintain the same order as their regions
    const sortedAirports = nonRegionAirports.sort((a, b) => {
      const regionA = groupedOptions.findIndex(g => 
        g.airports.some(airport => airport.code === a.code)
      );
      const regionB = groupedOptions.findIndex(g => 
        g.airports.some(airport => airport.code === b.code)
      );
      return regionA - regionB;
    });

    return [...regions, ...sortedAirports];
  }, [airports, groupedOptions]);

  // Handle selection changes in the Autocomplete component
  const handleChange = (event, newValue) => {
    if (newValue.length > 0) {
      // Only keep the last selected item (single selection behavior)
      const lastSelected = newValue[newValue.length - 1];
      onChange([lastSelected]);
    } else {
      onChange(null);
    }
  };

  // Check if a region is currently selected
  const isRegionSelected = (regionName) => {
    if (!value) return false;
    const region = groupedOptions.find(g => g.region.name === regionName);
    return value.some(v => v.code === region?.region.code);
  };

  // Handle clicking the "Select all"/"Deselect all" button for a region
  const handleRegionClick = (regionName, event) => {
    event.stopPropagation(); // Prevent event bubbling
    const region = groupedOptions.find(g => g.region.name === regionName);
    if (!region) return;

    const isSelected = isRegionSelected(regionName);
    if (isSelected) {
      onChange(null); // Deselect all
    } else {
      onChange([region.region]); // Select entire region
    }
  };

  // Filter function to show only actual airports (not regions) in the dropdown
  const filterOptions = (options) => {
    return options.filter(option => 
      option && !option.isRegion
    );
  };

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      value={value || []}
      onChange={handleChange}
      options={filteredOptions}
      filterOptions={filterOptions}
      sx={{
        '& .MuiAutocomplete-clearIndicator': {
          display: 'none'
        }
      }}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, value) => option.code === value.code}
      // Group airports by their region
      groupBy={(option) => {
        if (option.isRegion) return '';
        const region = groupedOptions.find(g => 
          g.airports.some(a => a.code === option.code)
        );
        return region ? region.region.name : '';
      }}
      // Custom render function for each option
      renderOption={(props, option, { selected }) => {
        const { key, ...otherProps } = props;
        
        // Determine if options should be disabled based on current selection
        const hasSelection = value && value.length > 0;
        const isSelected = Boolean(
          selected || (
            value?.[0]?.isRegion && 
            groupedOptions.find(g => g.region.code === value[0].code)?.airports
              .some(a => a.code === option.code)
          )
        );
        
        // Disable options when something else is selected
        const isDisabled = Boolean(
          hasSelection && (
            value[0]?.code !== option.code &&
            (!value[0]?.isRegion || !isSelected)
          )
        );

        // Skip rendering region options in the dropdown
        if (option.isRegion) return null;
        
        // Render airport option with checkbox
        return (
          <li 
            key={key} 
            {...otherProps}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingLeft: '32px',
              borderBottom: '1px solid var(--border-color)',
              opacity: isDisabled ? 0.5 : 1,
              pointerEvents: isDisabled ? 'none' : 'auto'
            }}
          >
            {option.name}
            <Checkbox 
              checked={isSelected}
              disabled={Boolean(isDisabled)}
            />
          </li>
        );
      }}
      // Custom render function for region groups
      renderGroup={(params) => (
        <div key={params.key}>
          <div style={{
            padding: '8px 16px',
            backgroundColor: 'var(--background-light)',
            borderBottom: '1px solid var(--border-color)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <strong style={{ 
                fontSize: '1.2rem',
                fontWeight: 'bold'
              }}>{params.group}</strong>
              <span 
                onClick={(e) => handleRegionClick(params.group, e)}
                style={{
                  color: 'var(--primary-color)',
                  cursor: 'pointer',
                  fontSize: '1.2rem',
                  textDecoration: 'none'
                }}
              >
                {isRegionSelected(params.group) ? 'Deselect all' : 'Select all'}
              </span>
            </div>
          </div>
          <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {params.children}
          </ul>
        </div>
      )}
      // Custom render function for the input field
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={value?.length === 0 ? "Select departure airports" : ""}
          fullWidth
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <span className="material-icons">flight_takeoff</span>
                {params.InputProps.startAdornment}
              </>
            ),
          }}
        />
      )}
      className="airport-input"
    />
  );
}

export default AirportInput;